// Returns to vendors (BL-013 PR 2), kept apart from the screens so they are tested: what a return
// says about itself, the draft's lines as the form edits them and as vendorReturns.create takes them,
// a credit note's lines from what is left to credit, and each way a return can close, in words.

import { money } from '../books/model.ts';
import { qtyText } from './model.ts';
import type { Po } from './orders-model.ts';

/** Who sees returns (vendors.view): the owner, managers, the warehouse and the bookkeeper. */
export const RETURN_PERMS = ['vendors.view'];

export type ReturnStatus = 'draft' | 'sent' | 'closed' | 'cancelled';
export type CloseHow = 'credited' | 'credited_less' | 'netted' | 'kept' | 'sent_back';

export interface ReturnLine {
  id: string;
  lineNo: number;
  itemId: string;
  description: string;
  poLineId: string | null;
  poNumber: string | null;
  quantity: string;
  unitCreditCents: string;
  expectedCents: number;
  tracked: boolean | null;
  valueCents: number | null;
  clearedCents: number;
  creditedQty: string;
}

export interface VendorReturn {
  id: string;
  number: string;
  vendorId: string;
  vendorName: string;
  locationId: string;
  locationName: string;
  returnType: string;
  returnTypeName: string;
  rma: string | null;
  note: string | null;
  status: ReturnStatus;
  sentOn: string | null;
  valueCents: number | null;
  clearedCents: number;
  openCents: number;
  expectedCents: number;
  closedOn: string | null;
  closedHow: CloseHow | null;
  closeNote: string | null;
  cancelReason: string | null;
  lines: ReturnLine[];
  credits: { id: string; number: string; vendorRef: string; status: string; totalCents: number }[];
}

export interface ReturnType {
  code: string;
  name: string;
  active: boolean;
}

/** vendorReturns.awaitingCredit's row. */
export interface AwaitingRow {
  returnId: string;
  number: string;
  vendorId: string;
  vendorName: string;
  rma: string | null;
  sentOn: string;
  daysOut: number;
  valueCents: number;
  openCents: number;
  expectedCents: number;
}

export const RETURN_FILTERS: { label: string; status: ReturnStatus | null }[] = [
  { label: 'All', status: null },
  { label: 'Drafts', status: 'draft' },
  { label: 'Sent back', status: 'sent' },
  { label: 'Closed', status: 'closed' },
  { label: 'Cancelled', status: 'cancelled' },
];

const CLOSED: Record<CloseHow, string> = {
  credited: 'Credited',
  credited_less: 'Credited less',
  netted: 'Netted on their bill',
  kept: 'Kept by the vendor',
  sent_back: 'Sent back to us',
};

/** The return's state in words: "Draft", "Sent back · waiting for credit", "Closed · Credited less". */
export function returnStatusText(r: Pick<VendorReturn, 'status' | 'closedHow'>): string {
  if (r.status === 'draft') return 'Draft';
  if (r.status === 'sent') return 'Sent back · waiting for credit';
  if (r.status === 'cancelled') return 'Cancelled';
  return `Closed · ${r.closedHow ? CLOSED[r.closedHow] : 'Credited'}`;
}

/** "2 × Ball valve", "1 × Tee (from PO-12)". */
export function lineText(l: Pick<ReturnLine, 'quantity' | 'description' | 'poNumber'>): string {
  return `${qtyText(l.quantity)} × ${l.description}${l.poNumber ? ` (from ${l.poNumber})` : ''}`;
}

/** "3 days out", "1 day out", "Sent today". */
export function daysOutText(days: number): string {
  if (days <= 0) return 'Sent today';
  return `${days} day${days === 1 ? '' : 's'} out`;
}

// ── the draft's lines ──

export interface LineForm {
  itemId: string;
  name: string;
  poLineId?: string;
  poNumber?: string;
  /** Each, as stock counts it. */
  quantity: string;
  /** Most that can go back on this line: what the PO line received, in each; none for loose stock. */
  most?: number;
  /** The credit expected each, in dollars as typed; empty: the PO's price, or what one is carried at. */
  credit: string;
}

const QTY = /^\d{1,9}(\.\d{1,3})?$/;
const MONEY = /^\d{1,7}(\.\d{1,2})?$/;

/** What is wrong with the lines, in words; nothing when they can be saved. */
export function lineProblems(lines: readonly LineForm[]): string[] {
  const out: string[] = [];
  if (!lines.some((l) => Number(l.quantity) > 0)) out.push('Add what goes back, and how many.');
  for (const l of lines) {
    if (l.quantity.trim() && (!QTY.test(l.quantity.trim()) || Number(l.quantity) < 0))
      out.push(`${l.name}: a quantity like 1 or 2.5.`);
    else if (l.most !== undefined && Number(l.quantity) > l.most)
      out.push(`${l.name}: ${qtyText(l.most)} came on ${l.poNumber ?? 'the PO'}.`);
    if (l.credit.trim() && !MONEY.test(l.credit.trim())) out.push(`${l.name}: a credit like 12.50.`);
  }
  return out;
}

/** vendorReturns.create's lines: those with a quantity; a typed credit in cents. */
export function linesInput(lines: readonly LineForm[]) {
  return lines
    .filter((l) => Number(l.quantity) > 0)
    .map((l) => ({
      itemId: l.itemId,
      quantity: l.quantity.trim(),
      ...(l.poLineId ? { poLineId: l.poLineId } : {}),
      ...(l.credit.trim() ? { creditCents: Math.round(Number(l.credit.trim()) * 100) } : {}),
    }));
}

/** A return from a PO: each line it received, nothing typed yet, at most what came, in each. */
export function linesFromPo(po: Pick<Po, 'number' | 'lines'>): LineForm[] {
  return po.lines
    .filter((l) => Number(l.receivedQty) > 0)
    .map((l) => ({
      itemId: l.itemId,
      name: l.description,
      poLineId: l.id,
      poNumber: po.number,
      quantity: '',
      most: Number((Number(l.receivedQty) * Number(l.unitsPerPurchase ?? '1')).toFixed(3)),
      credit: '',
    }));
}

// ── the vendor's credit note for a return ──

export interface CreditLineForm {
  returnLineId: string;
  name: string;
  /** Left to credit, each. */
  left: number;
  quantity: string;
  /** Each, in dollars. */
  price: string;
}

/**
 * A credit note's lines from what is left to credit on a return: every line not yet credited in full,
 * the rest of its quantity at the credit expected each. Credit notes still waiting for approval
 * already hold their quantities, and the server refuses more than went back.
 */
export function creditForm(r: Pick<VendorReturn, 'number' | 'lines'>): CreditLineForm[] {
  return r.lines
    .map((l) => ({ l, left: Number((Number(l.quantity) - Number(l.creditedQty)).toFixed(3)) }))
    .filter(({ left }) => left > 0)
    .map(({ l, left }) => ({
      returnLineId: l.id,
      name: l.description,
      left,
      quantity: qtyText(left),
      price: (Number(l.unitCreditCents) / 100).toFixed(2),
    }));
}

/** vendorBills.createCredit's lines, or what is wrong with them. */
export function creditLines(
  form: readonly CreditLineForm[],
): { lines: { returnLineId: string; quantity: string; unitCostCents: number }[] } | { problem: string } {
  const lines = [];
  for (const f of form) {
    if (!f.quantity.trim() || Number(f.quantity) === 0) continue;
    if (!QTY.test(f.quantity.trim())) return { problem: `${f.name}: a quantity like 1 or 2.5.` };
    if (Number(f.quantity) > f.left) return { problem: `${f.name}: ${qtyText(f.left)} is left to credit.` };
    if (!MONEY.test(f.price.trim())) return { problem: `${f.name}: a price each like 12.50.` };
    lines.push({
      returnLineId: f.returnLineId,
      quantity: f.quantity.trim(),
      unitCostCents: Math.round(Number(f.price.trim()) * 100),
    });
  }
  if (!lines.length) return { problem: 'Credit at least one line.' };
  return { lines };
}

/** What the credit's lines add up to before tax, in cents, as the server rounds them. */
export function creditSubtotal(lines: readonly { quantity: string; unitCostCents: number }[]): number {
  return lines.reduce((s, l) => s + Math.round(Number(l.quantity) * l.unitCostCents), 0);
}

// ── closing without a full credit ──

export type CloseChoice = 'credited_less' | 'netted' | 'kept';

/** Netting on their bill is only for parts that came on a PO. */
export const mayNet = (r: Pick<VendorReturn, 'lines'>) =>
  r.lines.every((l) => Number(l.creditedQty) >= Number(l.quantity) || l.poLineId !== null);

/** What closing does, before the bookkeeper decides. */
export function closeOutcome(r: Pick<VendorReturn, 'number' | 'openCents' | 'vendorName'>, how: CloseChoice) {
  const open = money(r.openCents);
  if (how === 'credited_less')
    return `${r.number} closes. The ${open} not credited goes to price differences: ${r.vendorName} won’t credit more.`;
  if (how === 'netted')
    return `${r.number} closes. ${r.vendorName} billed only what you kept, so it comes off what was received and not billed, at the PO’s price; any difference from the ${open} carried goes to price differences.`;
  return `${r.number} closes. The ${open} not credited is written off as a stock adjustment: ${r.vendorName} kept or scrapped the parts.`;
}

export const CLOSE_LABEL: Record<CloseChoice, string> = {
  credited_less: 'They credited less',
  netted: 'They left it off their bill',
  kept: 'They kept it, no credit',
};
